import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

import { creditsApi, usersApi } from "../../services/api";
import { Badge, Table, PageHeader, StatCard, Modal, Input, Button } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  Filter,
  Check,
  Eye,
  Copy,
  Calendar,
  ChevronDown,
  ChevronUp,
  Search,
  X,
} from "lucide-react";

// ── Date preset helper ────────────────────────────────────────────────────────
const DATE_PRESETS = [
  { label: "All Time",   value: "ALL_TIME"   },
  { label: "Today",      value: "TODAY"      },
  { label: "Yesterday",  value: "YESTERDAY"  },
  { label: "This Week",  value: "THIS_WEEK"  },
  { label: "This Month", value: "THIS_MONTH" },
];

function getPresetDates(preset) {
  const now   = new Date();
  const pad   = n => String(n).padStart(2, "0");
  const fmt   = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = fmt(now);
  if (preset === "TODAY")     return { fromDate: today, toDate: today };
  if (preset === "YESTERDAY") {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    return { fromDate: fmt(y), toDate: fmt(y) };
  }
  if (preset === "THIS_WEEK") {
    const s = new Date(now); s.setDate(now.getDate() - now.getDay());
    return { fromDate: fmt(s), toDate: today };
  }
  if (preset === "THIS_MONTH") {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    return { fromDate: fmt(s), toDate: today };
  }
  return { fromDate: "", toDate: "" };
}

// ── Date Column Header Dropdown ───────────────────────────────────────────────
// Positioning logic:
//   1. Preferred: align panel's LEFT edge to button's LEFT edge.
//   2. If that causes the right side to overflow viewport → shift left so the
//      panel's RIGHT edge sits 12px from the right viewport edge.
//   3. Never let the panel's left edge go below 12px from the left viewport edge.
// This keeps the panel floating directly over the table regardless of where the
// column header appears on screen.

const PANEL_WIDTH = 460;
const PANEL_MARGIN = 12; // min gap from viewport edges

function DateHeaderDropdown({ fromDate, toDate, onApply }) {
  const [open,         setOpen]         = useState(false);
  const [activePreset, setActivePreset] = useState("ALL_TIME");
  const [tempFrom,     setTempFrom]     = useState("");
  const [tempTo,       setTempTo]       = useState("");
  const [pos,          setPos]          = useState({ top: 0, left: 0 });
  const btnRef   = useRef(null);
  const panelRef = useRef(null);

  // Sync activePreset when parent clears filters
  useEffect(() => {
    if (!fromDate && !toDate) setActivePreset("ALL_TIME");
  }, [fromDate, toDate]);

  // Close on outside click / scroll / resize
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const outsideClick = (e) => {
      if (
        btnRef.current   && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", outsideClick);
    window.addEventListener("scroll",  close, true);
    window.addEventListener("resize",  close);
    return () => {
      document.removeEventListener("mousedown", outsideClick);
      window.removeEventListener("scroll",  close, true);
      window.removeEventListener("resize",  close);
    };
  }, [open]);

  const calcLeft = (btnRect) => {
    const viewportWidth = window.innerWidth;
    // Preferred: align left edges
    let left = btnRect.left + window.scrollX;
    // Would right side overflow?
    if (left + PANEL_WIDTH > viewportWidth - PANEL_MARGIN) {
      // Shift so right edge is at viewport right - margin
      left = viewportWidth - PANEL_WIDTH - PANEL_MARGIN + window.scrollX;
    }
    // Never clip the left edge
    if (left < PANEL_MARGIN + window.scrollX) {
      left = PANEL_MARGIN + window.scrollX;
    }
    return left;
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top:  rect.bottom + window.scrollY + 6,
        left: calcLeft(rect),
      });
    }
    setOpen(o => !o);
  };

  const isActive = fromDate || toDate;

  const applyPreset = (preset) => {
    setActivePreset(preset);
    const dates = getPresetDates(preset);
    onApply(dates.fromDate, dates.toDate);
  };

  const applyCustom = () => {
    setActivePreset("CUSTOM");
    onApply(tempFrom, tempTo);
    setOpen(false);
  };

  const reset = () => {
    setActivePreset("ALL_TIME");
    setTempFrom(""); setTempTo("");
    onApply("", "");
    setOpen(false);
  };

  return (
    <>
      {/* Column header button */}
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: "none", border: "none", cursor: "pointer", padding: 0,
          fontSize: 10, fontWeight: 700,
          color: isActive ? "#0F3D3E" : "var(--text-muted)",
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}
      >
        <Calendar
          size={11} strokeWidth={2.5}
          color={isActive ? "#0F3D3E" : "var(--text-muted)"}
          style={{ flexShrink: 0 }}
        />
        Date
        {open
          ? <ChevronUp   size={9} strokeWidth={3} />
          : <ChevronDown size={9} strokeWidth={3} />
        }
      </button>

      {/* Portal panel — smart-positioned */}
      {open && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "absolute",
            top:  pos.top,
            left: pos.left,
            zIndex: 99999,
            display: "flex",
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
            overflow: "hidden",
            width: PANEL_WIDTH,
            maxWidth: `calc(100vw - ${PANEL_MARGIN * 2}px)`,
          }}
        >
          {/* LEFT — Presets */}
          <div style={{
            width: 148, flexShrink: 0,
            borderRight: "1px solid var(--border)",
            padding: "8px 0",
            background: "var(--bg-input)",
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
              textTransform: "uppercase", letterSpacing: "0.6px",
              padding: "6px 16px 10px",
            }}>
              Quick Select
            </div>
            {DATE_PRESETS.map(p => {
              const isA = activePreset === p.value;
              return (
                <div
                  key={p.value}
                  onClick={() => applyPreset(p.value)}
                  onMouseEnter={e => { if (!isA) e.currentTarget.style.background = "var(--border)"; }}
                  onMouseLeave={e => { if (!isA) e.currentTarget.style.background = "transparent"; }}
                  style={{
                    padding: "9px 16px", cursor: "pointer",
                    fontSize: 13, fontWeight: isA ? 700 : 500,
                    color: isA ? "#0F3D3E" : "var(--text-secondary)",
                    background: isA ? "rgba(15,61,62,0.08)" : "transparent",
                    borderLeft: isA ? "3px solid #0F3D3E" : "3px solid transparent",
                    transition: "all 0.15s",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}
                >
                  {p.label}
                  {isA && <Check size={12} strokeWidth={3} color="#0F3D3E" />}
                </div>
              );
            })}
          </div>

          {/* RIGHT — Custom range */}
          <div style={{ flex: 1, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{
              fontSize: 13, fontWeight: 700,
              color: "var(--text-primary)",
              fontFamily: "var(--font-display)",
            }}>
              Custom Date Range
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
                  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5,
                }}>From</div>
                <input
                  type="date"
                  value={tempFrom}
                  onChange={e => { setTempFrom(e.target.value); setActivePreset("CUSTOM"); }}
                  style={{
                    width: "100%", padding: "7px 10px",
                    background: "var(--bg-input)", border: "1px solid var(--border)",
                    borderRadius: "var(--radius)", color: "var(--text-primary)",
                    fontSize: 12, outline: "none",
                    fontFamily: "var(--font-body)", boxSizing: "border-box",
                  }}
                  onFocus={e => e.target.style.borderColor = "#0F3D3E"}
                  onBlur={e  => e.target.style.borderColor = "var(--border)"}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
                  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5,
                }}>To</div>
                <input
                  type="date"
                  value={tempTo}
                  onChange={e => { setTempTo(e.target.value); setActivePreset("CUSTOM"); }}
                  style={{
                    width: "100%", padding: "7px 10px",
                    background: "var(--bg-input)", border: "1px solid var(--border)",
                    borderRadius: "var(--radius)", color: "var(--text-primary)",
                    fontSize: 12, outline: "none",
                    fontFamily: "var(--font-body)", boxSizing: "border-box",
                  }}
                  onFocus={e => e.target.style.borderColor = "#0F3D3E"}
                  onBlur={e  => e.target.style.borderColor = "var(--border)"}
                />
              </div>
            </div>

            {activePreset !== "CUSTOM" && (
              <div style={{
                fontSize: 11, color: "var(--text-muted)",
                background: "var(--bg-input)", borderRadius: "var(--radius)",
                padding: "7px 10px", border: "1px solid var(--border)",
                display: "flex", alignItems: "center", gap: 5,
              }}>
                <Calendar size={11} strokeWidth={2} />
                <span>
                  Showing:{" "}
                  <strong style={{ color: "var(--text-primary)" }}>
                    {DATE_PRESETS.find(p => p.value === activePreset)?.label}
                  </strong>
                </span>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: "auto" }}>
              <button
                type="button"
                onClick={reset}
                style={{
                  padding: "6px 14px", borderRadius: "var(--radius)",
                  border: "1px solid var(--border)", background: "var(--bg-card)",
                  color: "var(--text-secondary)", fontSize: 12, cursor: "pointer",
                }}
              >
                Reset
              </button>
              <button
                type="button"
                onClick={applyCustom}
                disabled={!tempFrom && !tempTo}
                style={{
                  padding: "6px 14px", borderRadius: "var(--radius)",
                  border: "none", background: "#0F3D3E", color: "#fff",
                  fontSize: 12, fontWeight: 600,
                  cursor: (!tempFrom && !tempTo) ? "not-allowed" : "pointer",
                  opacity: (!tempFrom && !tempTo) ? 0.5 : 1,
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ── Reusable column-header filter dropdown ────────────────────────────────────
function HeaderDropdown({ value, onChange, options, dotColors = {} }) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0 });
  const btnRef   = useRef(null);
  const panelRef = useRef(null);

  const activeColor = dotColors[value] || "#6b7280";

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (
        btnRef.current   && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top:  rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
    setOpen(o => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        title="Filter"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 22, height: 22, borderRadius: 4, cursor: "pointer", padding: 0,
          border: value ? `1px solid ${activeColor}88` : "1px solid var(--border)",
          background: value ? `${activeColor}18` : "transparent",
          color: value ? activeColor : "var(--text-muted)",
          transition: "all 0.18s",
        }}
      >
        <Filter
          size={11} strokeWidth={2.8}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s",
          }}
        />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "absolute", top: pos.top, left: pos.left,
            transform: "translateX(-50%)", zIndex: 99999,
            minWidth: 160, width: "max-content",
            background: "#0F3D3E", border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}
        >
          {options.map((opt) => {
            const isActive = opt.value === value;
            const dot      = dotColors[opt.value];
            return (
              <div
                key={String(opt.value)}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.10)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = isActive ? "rgba(255,255,255,0.12)" : "transparent"; }}
                style={{
                  padding: "9px 14px", cursor: "pointer", fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? "#fff" : "rgba(255,255,255,0.75)",
                  background: isActive ? "rgba(255,255,255,0.12)" : "transparent",
                  display: "flex", alignItems: "center", gap: 8,
                  transition: "background 0.15s", letterSpacing: "0.3px", whiteSpace: "nowrap",
                }}
              >
                {dot
                  ? <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                  : <span style={{ width: 7, height: 7, flexShrink: 0 }} />
                }
                {opt.label}
                {isActive && <Check size={12} strokeWidth={3} color="#fff" style={{ marginLeft: "auto" }} />}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

// ── Order Detail Modal ────────────────────────────────────────────────────────
function OrderDetail({ order, onClose, userMap, packMap, packTypeMap }) {
  if (!order) return null;

  const info         = typeof userMap[order.userId] === "object" ? userMap[order.userId] : null;
  const resolvedType = packTypeMap[order.packId] || order.packType || null;
  const hasDiscount  = order.hasDiscount;
  const statusLabel  = order.status === "CREATED" ? "Pending" : (order.status || "—");
  const statusColor  =
    order.status === "SUCCESS" ? "#4ade80" :
    order.status === "CREATED" ? "#fbbf24" :
    order.status === "FAILED"  ? "#f87171" : "var(--text-muted)";

  const Section = ({ title }) => (
    <div style={{
      fontSize: 10, fontWeight: 800, letterSpacing: "1px", textTransform: "uppercase",
      color: "var(--text-muted)", padding: "14px 0 6px",
      borderBottom: "1px solid var(--border)", marginBottom: 2,
    }}>
      {title}
    </div>
  );

  const Row = ({ label, value, mono = false, accent = null }) => (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "9px 0", borderBottom: "1px solid var(--border)",
    }}>
      <span style={{
        fontSize: 12, color: "var(--text-muted)", fontWeight: 500,
        textTransform: "uppercase", letterSpacing: "0.5px",
        flexShrink: 0, paddingRight: 12,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 13,
        color: accent || (mono ? "#7c3aed" : "var(--text-primary)"),
        fontFamily: mono ? "monospace" : "inherit",
        fontWeight: mono ? 700 : 500,
        wordBreak: "break-all", textAlign: "right", maxWidth: "62%",
        background: mono ? "rgba(124,58,237,0.06)" : "transparent",
        borderRadius: mono ? 4 : 0,
        padding: mono ? "1px 5px" : 0,
      }}>
        {value ?? "—"}
      </span>
    </div>
  );

  const packName =
    packMap[order.packId] && packMap[order.packId] !== "loading"
      ? packMap[order.packId]
      : order.packId?.slice(-8) || "—";

  const packTypeLabel =
    resolvedType === "IND_REGISTRATION_BONUS" ? "Reg. Bonus" :
    resolvedType === "REGULAR"                ? "Regular"    : "—";

  return (
    <Modal open={!!order} onClose={onClose} title="Order Details" width={540}>
      <Section title={<span style={{ fontSize: 11, fontWeight: 1000, color: "#0c0d0c" }}>Order Info</span>} />
      <Row label="Order ID"   value={order.txnId}     mono />
      <Row label="Order Date" value={order.createdAt ? new Date(order.createdAt).toLocaleString("en-IN") : "—"} />
      <Row label="Status"     value={statusLabel}      accent={statusColor} />

      <Section title={<span style={{ fontSize: 11, fontWeight: 1000, color: "#0c0d0c" }}>User</span>} />
      <Row label="Name"    value={info?.name || order.userId} />
      <Row label="User ID" value={info?.avId || "—"} mono={!!info?.avId} />

      <Section title={<span style={{ fontSize: 11, fontWeight: 1000, color: "#0c0d0c" }}>Pack</span>} />
      <Row label="Pack"      value={packName} />
      <Row label="Pack Type" value={packTypeLabel} />
      <Row label="Credits"   value={order.credits} accent="#4ade80" />

      <Section title={<span style={{ fontSize: 11, fontWeight: 1000, color: "#0c0d0c" }}>Pricing</span>} />
      <Row label="Actual Price" value={`₹${order.actualPrice ?? "—"}`} />
      <Row
        label="Discount"
        value={hasDiscount ? `${order.discountPercentage}% off  →  ₹${order.discountPrice}` : "No discount"}
        accent={hasDiscount ? "#f59e0b" : undefined}
      />
      <Row label="Final Price" value={`₹${order.finalPrice ?? "—"}`} accent="#34d399" />

      <Section title={<span style={{ fontSize: 11, fontWeight: 1000, color: "#0c0d0c" }}>Payment</span>} />
      <Row label="Payment Gateway" value={order.razorpayOrderId ? "Razorpay" : "—"} />

      <Section title={<span style={{ fontSize: 11, fontWeight: 1000, color: "#0c0d0c" }}>Device</span>} />
      <Row label="Device Type" value={order.deviceType || "—"} />
      <Row
        label="Device ID"
        value={order.deviceId ? `${order.deviceId.slice(0, 12)}…${order.deviceId.slice(-8)}` : "—"}
        mono={!!order.deviceId}
      />
    </Modal>
  );
}

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: "",        label: "All Status" },
  { value: "SUCCESS", label: "Success"    },
  { value: "CREATED", label: "Pending"    },
  { value: "FAILED",  label: "Failed"     },
];

const STATUS_DOT_COLORS = {
  SUCCESS: "#4ade80",
  CREATED: "#fbbf24",
  FAILED:  "#f87171",
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function CreditOrders() {
  const packsLoadedRef = useRef(false);
  const userMapRef     = useRef({});
  const packMapRef     = useRef({});
  const packTypeMapRef = useRef({});

  const [data,        setData]        = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [stats,       setStats]       = useState(null);
  const [search,      setSearch]      = useState("");
  const [status,      setStatus]      = useState("");
  const [packType,    setPackType]    = useState(null);
  const [page,        setPage]        = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [userMap,     setUserMap]     = useState({});
  const [packMap,     setPackMap]     = useState({});
  const [packTypeMap, setPackTypeMap] = useState({});

  // Date filter state
  const [fromDate, setFromDate] = useState("");
  const [toDate,   setToDate]   = useState("");

  // ── Resolve userIds ──────────────────────────────────────────────────────
  function resolveUserIds(orders) {
    const mongoIds   = [...new Set((orders.content || []).map(r => r.userId).filter(Boolean))];
    const unresolved = mongoIds.filter(id => !(id in userMapRef.current));
    if (unresolved.length === 0) return;

    unresolved.forEach(mongoId => {
      userMapRef.current[mongoId] = "loading";
      usersApi.getById(mongoId)
        .then(r => {
          const user     = r.data.data;
          const resolved = { name: user?.name || user?.mobile || user?.email || mongoId, avId: user?.userId || null };
          userMapRef.current[mongoId] = resolved;
          setUserMap(p => ({ ...p, [mongoId]: resolved }));
        })
        .catch(() => {
          const fallback = { name: mongoId, avId: null };
          userMapRef.current[mongoId] = fallback;
          setUserMap(p => ({ ...p, [mongoId]: fallback }));
        });
    });
  }

  // ── Resolve packIds ──────────────────────────────────────────────────────
  function resolvePackIds(orders) {
    const packIds    = [...new Set((orders.content || []).map(r => r.packId).filter(Boolean))];
    const unresolved = packIds.filter(id => !(id in packMapRef.current));
    if (unresolved.length === 0) return;

    unresolved.forEach(packId => {
      packMapRef.current[packId]     = "loading";
      packTypeMapRef.current[packId] = "loading";

      creditsApi.getPackById(packId)
        .then(r => {
          const pack         = r.data.data;
          const resolvedName = pack?.name     || packId;
          const resolvedType = pack?.packType || null;
          packMapRef.current[packId]     = resolvedName;
          packTypeMapRef.current[packId] = resolvedType;
          setPackMap(p     => ({ ...p, [packId]: resolvedName }));
          setPackTypeMap(p => ({ ...p, [packId]: resolvedType }));
        })
        .catch(() => {
          packMapRef.current[packId]     = packId;
          packTypeMapRef.current[packId] = null;
          setPackMap(p     => ({ ...p, [packId]: packId }));
          setPackTypeMap(p => ({ ...p, [packId]: null   }));
        });
    });
  }

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);

    const apiCalls = [
      creditsApi.getOrders(status || null, search, packType, page, fromDate || null, toDate || null),
      creditsApi.getOrderStats(),
    ];

    if (!packsLoadedRef.current) apiCalls.push(creditsApi.getPacks());

    Promise.all(apiCalls)
      .then(([o, s, p]) => {
        const orders = o.data.data;
        setData(orders);
        setStats(s.data.data);
        resolveUserIds(orders);
        resolvePackIds(orders);

        if (p && !packsLoadedRef.current) {
          const m = {}, mt = {};
          (p.data.data?.content || p.data.data || []).forEach(pack => {
            m[pack.id]  = pack.name;
            mt[pack.id] = pack.packType || null;
            packMapRef.current[pack.id]     = pack.name;
            packTypeMapRef.current[pack.id] = pack.packType || null;
          });
          setPackMap(m);
          setPackTypeMap(mt);
          packsLoadedRef.current = true;
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, packType, page, search, fromDate, toDate]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSearch = (e) => { e.preventDefault(); setPage(0); };

  const handleClear = () => {
    setSearch(""); setStatus(""); setPackType(null);
    setFromDate(""); setToDate(""); setPage(0);
  };

  const hasFilters = search || status || packType || fromDate || toDate;

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      key: "id", label: "Sr.",
      render: (v) => {
        const index = data.content.findIndex(item => item.id === v);
        return (
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: "var(--text-muted)", minWidth: 28, display: "inline-block",
          }}>
            {page * 20 + index + 1}
          </span>
        );
      },
    },

    // Date column — header contains the DateHeaderDropdown
    {
      key: "createdAt",
      label: (
        <DateHeaderDropdown
          fromDate={fromDate}
          toDate={toDate}
          onApply={(from, to) => { setFromDate(from); setToDate(to); setPage(0); }}
        />
      ),
      render: (v) => v
        ? <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", whiteSpace: "nowrap" }}>
            {new Date(v).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
          </span>
        : "—",
    },

    {
      key: "userId", label: "User",
      render: (v) => {
        const name = userMap[v]?.name ?? null;
        return (
          <span style={{
            fontSize: 12, fontWeight: 700, fontFamily: "monospace",
            color: "#0F3D3E", background: "rgba(15,61,62,0.08)",
            border: "1px solid rgba(15,61,62,0.15)", borderRadius: 5,
            padding: "2px 8px", whiteSpace: "nowrap",
          }}>
            {name && name !== "loading"
              ? name
              : <span style={{ color: "var(--text-muted)", fontSize: 11 }}>…</span>
            }
          </span>
        );
      },
    },

    {
      key: "userId", label: "User ID",
      render: (v) => {
        const avId = userMap[v]?.avId ?? null;
        return avId ? (
          <span style={{
            fontSize: 11, fontWeight: 700, fontFamily: "monospace",
            color: "#0F3D3E", background: "rgba(15,61,62,0.08)",
            border: "1px solid rgba(15,61,62,0.15)",
            borderRadius: 20, padding: "2px 10px",
            display: "inline-block", letterSpacing: "0.3px",
          }}>
            {avId}
          </span>
        ) : <span style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>;
      },
    },

    {
      key: "packId", label: "Pack",
      render: (v) => (
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
          {packMap[v] && packMap[v] !== "loading"
            ? packMap[v]
            : <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{v?.slice(-8)}</span>
          }
        </span>
      ),
    },

    {
      key: "txnId", label: "Order ID",
      render: (v) => v ? (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{
            fontSize: 11, fontFamily: "monospace", fontWeight: 700,
            color: "#7c3aed", background: "rgba(227,17,216,0.07)",
            border: "1px solid rgba(124,58,237,0.18)", borderRadius: 5,
            padding: "2px 8px", whiteSpace: "nowrap",
          }}>{v}</span>
          <button
            type="button"
            title="Copy Order ID"
            onClick={() => {
              navigator.clipboard.writeText(v)
                .then(() => showToast("Order ID copied!", "success"))
                .catch(() => showToast("Failed to copy", "error"));
            }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "2px 4px", borderRadius: 4, lineHeight: 1,
              color: "var(--text-muted)", display: "flex",
              alignItems: "center", justifyContent: "center",
              transition: "color 0.15s, background 0.15s", flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "#7c3aed"; e.currentTarget.style.background = "rgba(124,58,237,0.08)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.background = "none"; }}
          >
            <Copy size={13} strokeWidth={2} />
          </button>
        </div>
      ) : <span style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>,
    },

    {
      key: "amount", label: "Amount",
      render: (v) => (
        <span style={{ color: "var(--teal-300)", fontWeight: 700, fontSize: 13 }}>₹{v}</span>
      ),
    },

    {
      key: "status",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Status</span>
          <HeaderDropdown
            value={status}
            onChange={(v) => { setStatus(v); setPage(0); }}
            options={STATUS_OPTIONS}
            dotColors={STATUS_DOT_COLORS}
          />
        </div>
      ),
      render: (v) => {
        const label   = v === "CREATED" ? "Pending" : v;
        const variant = v === "SUCCESS" ? "success" : v === "CREATED" ? "warning" : "danger";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },

    {
      key: "id", label: "Details",
      render: (_, row) => (
        <button
          onClick={() => setSelected(row)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#9ca3af", display: "inline-flex", alignItems: "center",
            padding: 2, borderRadius: 4, transition: "color 0.18s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "#0F3D3E"}
          onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
        >
          <Eye size={16} strokeWidth={2} />
        </button>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <PageHeader title="Credit Orders" subtitle="All credit purchase transactions" />

      {/* Stat Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14, marginBottom: 28 }}>
        <StatCard title="Total"   value={stats?.total   ?? "—"} gradient="linear-gradient(135deg,rgba(15,61,62,0.55),rgba(15,61,62,0.25))"    loading={!stats} />
        <StatCard title="Success" value={stats?.success ?? "—"} gradient="linear-gradient(135deg,rgba(34,197,94,0.55),rgba(34,197,94,0.25))"   loading={!stats} />
        <StatCard title="Pending" value={stats?.created ?? "—"} gradient="linear-gradient(135deg,rgba(208,179,106,0.6),rgba(208,179,106,0.25))" loading={!stats} />
        <StatCard title="Failed"  value={stats?.failed  ?? "—"} gradient="linear-gradient(135deg,rgba(239,68,68,0.5),rgba(239,68,68,0.2))"     loading={!stats} />
      </div>

      {/* Search bar */}
      <form
        onSubmit={handleSearch}
        style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}
      >
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <Search
            size={14} strokeWidth={2}
            style={{
              position: "absolute", left: 10, top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)", pointerEvents: "none",
            }}
          />
          <input
            placeholder="Search by order ID"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "9px 14px 9px 32px",
              borderRadius: "var(--radius)", border: "1px solid var(--border)",
              background: "var(--bg-input)", color: "var(--text-primary)",
              fontSize: 13, fontFamily: "var(--font-body)", outline: "none",
            }}
          />
        </div>
        <Button type="submit">Search</Button>
        {hasFilters && (
          <Button variant="ghost" type="button" onClick={handleClear}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <X size={13} strokeWidth={2.5} /> Clear all
            </span>
          </Button>
        )}
      </form>

      {/* Table */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)", overflow: "hidden",
      }}>
        {/* Table header bar */}
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            {data.totalElements} orders
          </span>

          {/* Active filter chips */}
          {status && (
            <span style={{
              fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E",
              borderRadius: 20, padding: "2px 10px", fontWeight: 600,
            }}>
              {STATUS_OPTIONS.find(o => o.value === status)?.label}
            </span>
          )}
          {(fromDate || toDate) && (
            <span style={{
              fontSize: 11, background: "rgba(15,61,62,0.1)", color: "#0F3D3E",
              borderRadius: 20, padding: "2px 10px", fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 4,
            }}>
              <Calendar size={10} strokeWidth={2.5} />
              {fromDate || "…"} → {toDate || "today"}
            </span>
          )}
        </div>

        {loading
          ? <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
              <div style={{
                width: 32, height: 32, margin: "0 auto 10px",
                border: "3px solid var(--border)",
                borderTop: "3px solid #0F3D3E",
                borderRadius: "50%",
                animation: "spin 0.7s linear infinite",
              }} />
              Loading…
            </div>
          : <Table columns={columns} data={data.content} emptyText="No orders found" />
        }

        {/* Pagination */}
        {(() => {
          const totalPages = data.totalPages;
          if (totalPages <= 1 || data.totalElements === 0) return null;

          const getPages = () => {
            if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
            const pages = [];
            if (page <= 3) {
              pages.push(0, 1, 2, 3, 4, "...", totalPages - 1);
            } else if (page >= totalPages - 4) {
              pages.push(0, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1);
            } else {
              pages.push(0, "...", page - 1, page, page + 1, "...", totalPages - 1);
            }
            return pages;
          };

          const btnStyle = (isActive, isDisabled) => ({
            minWidth: 34, height: 34, padding: "0 10px",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            border: isActive ? "none" : "1px solid var(--border)",
            borderRadius: 8,
            background: isActive ? "#0F3D3E" : "transparent",
            color: isActive ? "#fff" : isDisabled ? "var(--text-muted)" : "var(--text-primary)",
            fontSize: 13, fontWeight: isActive ? 700 : 500,
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.45 : 1,
            transition: "all 0.15s",
            fontFamily: "var(--font-body)",
          });

          return (
            <div style={{
              padding: "14px 20px", borderTop: "1px solid var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <button
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                style={btnStyle(false, page === 0)}
              >← Prev</button>

              {getPages().map((pg, i) =>
                pg === "..." ? (
                  <span
                    key={`dots-${i}`}
                    style={{ padding: "0 4px", color: "var(--text-muted)", fontSize: 14, userSelect: "none" }}
                  >…</span>
                ) : (
                  <button
                    key={pg}
                    onClick={() => setPage(pg)}
                    style={btnStyle(page === pg, false)}
                    onMouseEnter={e => { if (page !== pg) e.currentTarget.style.background = "rgba(15,61,62,0.08)"; }}
                    onMouseLeave={e => { if (page !== pg) e.currentTarget.style.background = "transparent"; }}
                  >
                    {pg + 1}
                  </button>
                )
              )}

              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
                style={btnStyle(false, page >= totalPages - 1)}
              >Next →</button>
            </div>
          );
        })()}
      </div>

      <OrderDetail
        order={selected}
        onClose={() => setSelected(null)}
        userMap={userMap}
        packMap={packMap}
        packTypeMap={packTypeMap}
      />
    </div>
  );
}